import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button,
  Flex,
  Popconfirm,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useUnit } from 'effector-react'
import moment from 'moment'
import { useUrlFilters } from 'src/libs/useUrlFilters'
import { BlockAnyGateway } from '../blockAnyGateway'
import {
  $pendingGatewayId,
  BlockReasonModal,
  openBlockModal,
  submitGatewayBlock,
} from '../blockGateway'
import {
  STATUS_OPTIONS,
  FILTER_CONFIG_GATEWAYS,
  DEFAULT_ORDER,
  EMPTY_VALUE,
  PAGE_SIZE,
} from '../const'
import { ActiveFilterTag, FiltersPanel, FilterSearchInput } from '../filters'
import { useGatewayFilterOptions } from '../hooks'
import { ClientGateway } from '../types'
import { useClientGatewayList } from './hooks'
import { clientGatewaysLoad } from './model'

const { Text } = Typography

export const ClientGatewayList: React.FC = () => {
  const { clientID = '' } = useParams<{ clientID: string }>()
  const { filters, setFilters, resetFilters } = useUrlFilters(
    FILTER_CONFIG_GATEWAYS,
  )
  const { providerOptions, typeOptions } = useGatewayFilterOptions()
  const { gateways, total, loading } = useClientGatewayList()
  const pendingGatewayId = useUnit($pendingGatewayId)

  const {
    gatewayId,
    internalName,
    processingProvider,
    paymentMethodType,
    status,
    sorterField,
    page,
    pageSize,
  } = filters

  useEffect(() => {
    clientGatewaysLoad({
      clientId: clientID,
      gatewayId,
      internalName,
      processingProvider,
      paymentMethodType,
      status,
      sorterField,
      page,
      pageSize,
    })
  }, [
    clientID,
    gatewayId,
    internalName,
    processingProvider,
    paymentMethodType,
    status,
    sorterField,
    page,
    pageSize,
  ])

  const setFilter = (key: string, value?: string) => {
    setFilters({ [key]: value || undefined, page: '1' })
  }

  const handleResetFilters = () => {
    resetFilters()
    // `resetFilters` keeps pagination keys, which would strand the user on a
    // page that no longer exists once the filters are gone.
    setFilters({ page: '1' })
  }

  const activeTags = useMemo<ActiveFilterTag[]>(() => {
    const tags: ActiveFilterTag[] = []

    if (gatewayId)
      tags.push({
        key: 'gatewayId',
        label: `Gateway ID: ${gatewayId}`,
        clear: () => setFilter('gatewayId'),
      })
    if (internalName)
      tags.push({
        key: 'internalName',
        label: `Internal Name: ${internalName}`,
        clear: () => setFilter('internalName'),
      })
    if (processingProvider)
      tags.push({
        key: 'processingProvider',
        label: `Provider: ${processingProvider}`,
        clear: () => setFilter('processingProvider'),
      })
    if (paymentMethodType)
      tags.push({
        key: 'paymentMethodType',
        label: `Type: ${paymentMethodType}`,
        clear: () => setFilter('paymentMethodType'),
      })
    if (status)
      tags.push({
        key: 'status',
        label: `Status: ${status === 'blocked' ? 'Blocked' : 'Active'}`,
        clear: () => setFilter('status'),
      })

    return tags
  }, [gatewayId, internalName, processingProvider, paymentMethodType, status])

  const columns: ColumnsType<ClientGateway> = [
    {
      title: 'Gateway ID',
      dataIndex: 'gatewayId',
      width: 110,
    },
    {
      title: 'Internal Name',
      dataIndex: 'internalName',
    },
    {
      title: 'Provider',
      dataIndex: 'processingProvider',
    },
    {
      title: 'Type',
      dataIndex: 'paymentMethodType',
    },
    {
      title: 'Status',
      dataIndex: 'isBlocked',
      width: 100,
      render: (isBlocked: boolean) =>
        isBlocked ? (
          <Tag color="red" style={{ fontSize: 10, fontWeight: 700 }}>
            Blocked
          </Tag>
        ) : (
          <Tag color="success" style={{ fontSize: 10, fontWeight: 700 }}>
            Active
          </Tag>
        ),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      render: (reason: string | null, gateway) =>
        gateway.isBlocked && reason ? reason : EMPTY_VALUE,
    },
    {
      title: 'Updated At',
      dataIndex: 'changedStatusAt',
      width: 160,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: sorterField === 'changedStatusAt' ? 'ascend' : 'descend',
      render: (changedStatusAt: string | null) =>
        changedStatusAt ? (
          <>
            {moment.parseZone(changedStatusAt).format('YYYY-MM-DD')}{' '}
            <Text type="secondary">
              {moment.parseZone(changedStatusAt).format('HH:mm')}
            </Text>
          </>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      title: 'Manager',
      dataIndex: 'manager',
      render: (manager: string | null) => manager ?? EMPTY_VALUE,
    },
    {
      title: 'Was auto-blocked',
      dataIndex: 'isAuto',
      width: 140,
      render: (isAuto: boolean | null) => {
        if (isAuto === null) return EMPTY_VALUE
        return isAuto ? 'Yes' : 'No'
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, gateway: ClientGateway) => {
        const isPending = pendingGatewayId === gateway.gatewayId
        // One request at a time — two blocks fired back to back would race
        // over the same client's status.
        const isDisabled = pendingGatewayId !== null && !isPending

        if (gateway.isBlocked) {
          return (
            <Popconfirm
              title="Unblock this gateway?"
              okText="Unblock"
              cancelText="Cancel"
              onConfirm={() =>
                submitGatewayBlock({
                  clientId: clientID,
                  gatewayId: gateway.gatewayId,
                  isBlocked: false,
                })
              }
            >
              <Button size="small" loading={isPending} disabled={isDisabled}>
                Active
              </Button>
            </Popconfirm>
          )
        }

        return (
          <Button
            size="small"
            danger
            loading={isPending}
            disabled={isDisabled}
            onClick={() => openBlockModal(gateway)}
          >
            Block
          </Button>
        )
      },
    },
  ]

  return (
    <>
      <FiltersPanel
        rows={[
          <Flex gap={6} wrap="wrap" justify="space-between">
            <Flex gap={6} wrap="wrap">
              <FilterSearchInput
                placeholder="Gateway ID"
                value={gatewayId ?? ''}
                onApply={(value) => setFilter('gatewayId', value)}
                width={160}
                numericOnly
              />
              <FilterSearchInput
                placeholder="Internal Name"
                value={internalName ?? ''}
                onApply={(value) => setFilter('internalName', value)}
                width={260}
              />
            </Flex>
            <BlockAnyGateway />
          </Flex>,
          <Flex gap={10} wrap="wrap" align="center">
            <Select
              placeholder="Provider"
              value={processingProvider || undefined}
              onChange={(value) => setFilter('processingProvider', value)}
              options={providerOptions}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
            />
            <Select
              placeholder="Type"
              value={paymentMethodType || undefined}
              onChange={(value) => setFilter('paymentMethodType', value)}
              options={typeOptions}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
            />
            <Select
              placeholder="Status"
              value={status || undefined}
              onChange={(value) => setFilter('status', value)}
              options={STATUS_OPTIONS}
              allowClear
              style={{ width: 140 }}
            />
          </Flex>,
        ]}
        tags={activeTags}
        onReset={handleResetFilters}
      />

      <Table
        dataSource={gateways}
        className="overflow-auto"
        loading={loading}
        rowKey="gatewayId"
        columns={columns}
        pagination={{
          current: Number(page ?? 1),
          pageSize: Number(pageSize ?? PAGE_SIZE),
          showSizeChanger: true,
          total,
          position: ['bottomCenter'],
        }}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
        onChange={(pagination, _, sorter: any) => {
          setFilters({
            page: String(pagination.current ?? 1),
            pageSize: String(pagination.pageSize ?? PAGE_SIZE),
            sorterField:
              sorter?.order === 'ascend' ? 'changedStatusAt' : DEFAULT_ORDER,
          })
        }}
      />

      <BlockReasonModal clientId={clientID} />
    </>
  )
}
